import type { PubSubEvent } from "@shared/utils.events";
import { dispatch } from "@shared/utils.events";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

import { Alert, AlertProps } from "./Alert";
import { useAlertContext } from "./Context";

export type AlertProvidedProps = {
  trackingLocation: string;
  trackFn: typeof dispatch;
};

export const AlertProvided: React.FC<AlertProvidedProps> = ({
  trackingLocation,
  trackFn
}): JSX.Element => {
  const alertContext = useAlertContext();
  if (!alertContext) {
    console.warn("No Alert Context provided for the `AlertProvided` component");
    return <></>;
  }
  const { alert, closeAlert } = alertContext;

  const { inView, ref } = useInView({
    threshold: 1,
    triggerOnce: true
  });

  const track = (
    alert: AlertProps | null,
    location: string,
    kind: PubSubEvent,
    dismiss = false
  ) => {
    if (!alert?.title) return;
    trackFn(kind, {
      target: alert.title,
      location,
      ...(dismiss && { details: "Alert dismissed" })
    });
  };

  useEffect(() => {
    if (alert && inView) {
      track(alert, trackingLocation, "page element viewed");
    }
  }, [alert, inView]);

  const handleOnDismiss = () => {
    alert?.dismissOnClick?.();
    track(alert, trackingLocation, "page element clicked", true);
    closeAlert();
  };
  if (alert === null) return <></>;
  const props = { ...alert, dismissOnClick: handleOnDismiss } as AlertProps;
  return (
    <div ref={ref}>
      <Alert {...props} />
    </div>
  );
};
